import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  DragDropModule,
  moveItemInArray,
  transferArrayItem,
} from '@angular/cdk/drag-drop';
import {
  BehaviorSubject,
  finalize,
  Observable,
  Subject,
  take,
  takeUntil,
} from 'rxjs';

import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  NzFormControlComponent,
  NzFormItemComponent,
  NzFormLabelComponent,
  NzFormModule,
} from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { SvgIconComponent } from 'angular-svg-icon';

import { DRAWER_IDS } from '../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../core/services/drawer.service';
import { CommonService } from '../../../shared/services/common.service';
import { ErrorResponse } from '../../../core/models/error.models';
import { LookupItem } from '../../../core/models/lookup.models';
import { SECTION_DISPLAY_NAMES, TemplateMode } from '../bip-template.enums';
import {
  BehaviorQuestionnaires,
  EditDrawerParams,
  TemplateField,
  TemplateModel,
  TemplateQuestionResponse,
  TemplateSection,
} from '../bip-template.interface';
import { BipTemplateService } from '../bip-template.service';
import { CustomQuestionService } from '../../manage-settings/custom-question/custom-question.service';
import {
  PlanPayload,
  QuestionItem,
} from '../../manage-settings/manage-settings.interface';
import { ManageSettingsService } from '../../manage-settings/manage-settings.service';
import {
  BehaviorQuestionnairePayload,
  BipTemplateSavePayload,
  TemplateFieldPayload,
  TemplateQuestionItem,
  TemplateQuestionPayload,
  TemplateSectionPayload,
} from './bip-template-form.interface';
import { OrganizationSelectionService } from '../../../core/services/organization-selection.service';
import { APP_LOOKUP_IDS } from '../../insights/behavior-intervention-plan/view-bip-case/print-bip/behavior-section-and-lookup-mapping.constant';
import { AuditContextService } from '../../audit-logs/audit-context.service';
import { AuditFieldChange } from '../../audit-logs/audit-logs.interface';
import { BipTemplateGatingService } from '../../../shared/services/bip-template-gating.service';

const STATIC_SECTION_NAMES = [
  'Challenging Behavior',
  'Custom Questions',
  'Behavior Screener',
  'Strategy Details',
  'Reports',
  'Questionnaires',
] as const;

// These sections have a sub-item list (questions/plans) — their Required and
// Hard Stop are never set directly by the user; they're derived from the
// sub-items (any sub-item Required/Hard Stop makes the section Required/Hard
// Stop) and the checkboxes stay disabled at all times.
const DYNAMIC_GATING_SECTION_NAMES = new Set<string>([
  'Challenging Behavior',
  'Custom Questions',
  'Questionnaires',
]);

// Behavior Types is the only pinned behavior-definition question — always
// Required, never reorderable, always shown first (see bip-template/CLAUDE.md).
// Operational Definition used to be pinned here too but now behaves like any
// other native Challenging Behavior question: editable flags, freely
// reorderable among the non-fixed questions.
const FIXED_CHALLENGING_BEHAVIOR_QUESTIONS = [
  { sourceId: APP_LOOKUP_IDS.CaseBehaviorTypes, label: 'Behavior Types' },
] as const;

const TEMPLATE_FIELD_LABELS = [
  'Student Date of Birth',
  'Student School',
  'Behavior Screener',
  'Plan Created',
  'Last Revised',
] as const;

@Component({
  selector: 'app-bip-template-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DragDropModule,
    NzSpinModule,
    NzCheckboxModule,
    NzSwitchModule,
    NzButtonModule,
    NzFormModule,
    NzFormControlComponent,
    NzFormItemComponent,
    NzFormLabelComponent,
    NzInputModule,
    NzToolTipModule,
    CdkDropList,
    CdkDrag,
    SvgIconComponent,
  ],
  templateUrl: './bip-template-form.component.html',
  styleUrl: './bip-template-form.component.scss',
})
export class BipTemplateFormComponent implements OnInit, OnDestroy {
  private readonly drawerService = inject(DrawerService);
  private readonly bipTemplateService = inject(BipTemplateService);
  private readonly bipTemplateGatingService = inject(BipTemplateGatingService);
  private readonly commonService = inject(CommonService);
  private readonly fb = inject(FormBuilder);
  private readonly customQuestionService = inject(CustomQuestionService);
  private readonly manageSettingsService = inject(ManageSettingsService);
  private readonly orgSelectionService = inject(OrganizationSelectionService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly auditContextService = inject(AuditContextService);

  private readonly destroy$ = new Subject<void>();
  // Scoped to this drawer instance — deliberately not the shared root
  // LoaderService, since this form is opened as a drawer overlay on top of
  // other pages (e.g. bip-template-listing) that bind their own spinner to
  // that same global subject; sharing it caused an NG0100 the moment both
  // spinners were live at once.
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  // Several loads run concurrently (getCustomQuestions + getQuestionnaireItems
  // fire in parallel from applyTemplateData) or chained (getChallengingBehaviorLookup
  // -> loadTemplateById). A plain boolean flips to false the instant whichever
  // call happens to finish first, briefly re-enabling Save while the others are
  // still in flight — a submit at that moment sends a payload built from
  // still-empty arrays (missing questionnaire/custom-question sections). A
  // pending-request counter keeps loading$ true until every in-flight call
  // completes.
  private pendingRequests = 0;

  templateForm!: FormGroup;
  loading$: Observable<boolean> = this.loadingSubject.asObservable();

  drawerParams: EditDrawerParams = {
    selectedTemplate: null,
    mode: TemplateMode.CREATE,
    organizationId: 0,
  };

  orgId: number | null = 0;
  templateId: number | null = null;
  templateData: TemplateModel | null = null;

  originalBehaviorLookup: LookupItem[] = [];
  challengingBehaviorQuestions: TemplateQuestionItem[] = [];
  customQuestions: TemplateQuestionItem[] = [];
  questionnaireItems: TemplateQuestionItem[] = [];
  lastSubmittedPayload: BipTemplateSavePayload | null = null;

  private originalTemplateQuestionIds: number[] = [];
  private originalTemplateQuestionSettings = new Map<
    number,
    TemplateQuestionResponse
  >();
  private originalQuestionnaireSelections = new Map<string, boolean>();
  private originalQuestionnaireSequence = new Map<string, number>();
  private originalQuestionnaireGating = new Map<
    string,
    { isRequired: boolean; isHardStop: boolean; helpText: string }
  >();

  readonly openHelpKeys = new Set<string>();

  constructor() {
    this.initForm();
  }

  ngOnInit(): void {
    this.orgSelectionService.parentOrgId$
      .pipe(takeUntil(this.destroy$))
      .subscribe(parentOrgId => {
        if (parentOrgId) {
          this.orgId = parentOrgId;
          this.templateForm.patchValue({ organizationId: this.orgId });
        }
      });
    this.drawerService
      .getParams<EditDrawerParams>(DRAWER_IDS.ADD_TEMPLATE_V2)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.drawerParams = params;
          this.templateId = params.selectedTemplate;
        }

        // View mode reuses the same edit/create form read-only — disabling
        // the reactive form here (rather than per-control) covers every
        // formControlName-bound input in one place; question-level items
        // (challenging behavior/custom/questionnaire) live outside the
        // reactive form and are guarded individually (see isViewMode checks
        // in the toggle/drag handlers and template bindings).
        if (this.isViewMode) {
          this.templateForm.disable({ emitEvent: false });
        }

        // Challenging Behavior labels must be loaded before template data is
        // applied — applyBehaviorQuestionnaires() resolves section-header
        // names from originalBehaviorLookup, so running it before this lookup
        // resolves left header names blank whenever getTemplateById won the
        // race. Sequencing via the callback guarantees load order instead of
        // relying on which request happens to finish first.
        this.getChallengingBehaviorLookup(() => {
          if (
            (this.isEditMode || this.isViewMode || this.isCopyMode) &&
            this.templateId
          ) {
            this.loadTemplateById(this.templateId);
          } else {
            this.getQuestionnaireItems();
            if (this.orgId) {
              this.getCustomQuestions();
            }
          }
        });
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadingSubject.complete();
  }

  get isEditMode(): boolean {
    return this.drawerParams.mode === TemplateMode.EDIT;
  }

  get isViewMode(): boolean {
    return this.drawerParams.mode === TemplateMode.VIEW;
  }

  get isCopyMode(): boolean {
    return this.drawerParams.mode === TemplateMode.COPY;
  }

  get templateFields(): FormArray {
    return this.templateForm.get('templateFields') as FormArray;
  }

  get templateFieldControls(): FormGroup[] {
    return this.templateFields.controls as FormGroup[];
  }

  get sections(): FormArray {
    return this.templateForm.get('sections') as FormArray;
  }

  get isFormValid(): boolean {
    if (!this.templateForm.get('templateName')?.valid) {
      return false;
    }

    if (!this.isAnySectionSelected) {
      return false;
    }

    if (!this.isTemplateFieldsValid) {
      return false;
    }

    if (!this.isChallengingBehaviorSatisfied) {
      return false;
    }

    if (!this.isCustomQuestionsSatisfied) {
      return false;
    }

    return this.isQuestionnairesSatisfied;
  }

  get isAnySectionSelected(): boolean {
    return this.sections.controls.some(ctrl => this.isSectionSelected(ctrl));
  }

  get isTemplateFieldsValid(): boolean {
    return this.templateFieldControls.some(ctrl => ctrl.value.isSelected);
  }

  get isChallengingBehaviorSatisfied(): boolean {
    const challengingBehavior = this.sections.controls.find(
      ctrl => ctrl.value.sectionName === 'Challenging Behavior'
    );

    if (!challengingBehavior || !this.isSectionSelected(challengingBehavior)) {
      return true;
    }

    return this.challengingBehaviorQuestions.some(item => item.isIncluded);
  }

  get isCustomQuestionsSatisfied(): boolean {
    const customQuestionsSection = this.getSectionControl('Custom Questions');

    if (
      !customQuestionsSection ||
      !this.isSectionSelected(customQuestionsSection)
    ) {
      return true;
    }

    // Scoped to customQuestions only (not templateQuestionCandidates, which
    // also counts custom questions dragged into Challenging Behavior) — if
    // every custom question has been moved out of this section, the section
    // itself has nothing to show and must not validate as satisfied just
    // because a custom question exists somewhere else in the template.
    return this.customQuestions.some(item => item.isIncluded);
  }

  get isQuestionnairesSatisfied(): boolean {
    const questionnairesSection = this.getSectionControl('Questionnaires');

    if (
      !questionnairesSection ||
      !this.isSectionSelected(questionnairesSection)
    ) {
      return true;
    }

    return this.questionnaireItems.some(item => item.isIncluded);
  }

  // Every question that can end up in the templateQuestions payload — the
  // ones still listed under Custom Questions, plus any custom question that
  // has been dragged into Challenging Behavior (see mapBehaviorQuestionnairesForPayload).
  private get templateQuestionCandidates(): TemplateQuestionItem[] {
    return [
      ...this.customQuestions,
      ...this.challengingBehaviorQuestions.filter(
        item => item.source === 'custom'
      ),
    ];
  }

  // Reads the control's own value rather than the parent FormGroup's aggregate
  // `.value` — disabling a control (see buildSectionGroup, e.g. Challenging
  // Behavior's permanently-on isSelected) excludes it from the parent's
  // `.value`, which would otherwise hide the section subsection once its
  // `isSelected` control is disabled.
  isSectionSelected(sectionGroup: AbstractControl): boolean {
    return Boolean(sectionGroup.get('isSelected')?.value);
  }

  getSectionDisplayName(sectionName: string): string {
    return SECTION_DISPLAY_NAMES[sectionName] ?? sectionName;
  }

  // Surfaces each section's own validation message beside its title instead
  // of in a single block at the bottom of the form, so the user can see which
  // section needs attention without scanning the whole drawer.
  getSectionErrorMessage(sectionGroup: AbstractControl): string | null {
    if (this.isViewMode || !this.isSectionSelected(sectionGroup)) {
      return null;
    }

    switch (sectionGroup.value.sectionName) {
      case 'Challenging Behavior':
        return this.isChallengingBehaviorSatisfied
          ? null
          : 'Select at least one challenging behavior question';
      case 'Custom Questions':
        return this.isCustomQuestionsSatisfied
          ? null
          : 'Select at least one custom question';
      case 'Questionnaires':
        return this.isQuestionnairesSatisfied
          ? null
          : 'Select at least one plan';
      default:
        return null;
    }
  }

  private initForm(): void {
    this.templateForm = this.fb.group({
      templateName: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(250),
        ],
      ],
      isDefault: [false],
      isIncludeStrategyGuidelines: [false],
      organizationId: [this.orgId],
      templateFields: this.fb.array(
        TEMPLATE_FIELD_LABELS.map(label =>
          this.fb.group({ id: 0, fieldName: label, isSelected: false })
        )
      ),
      sections: this.fb.array(
        STATIC_SECTION_NAMES.map((name, index) =>
          this.buildSectionGroup(
            name,
            index + 1,
            0,
            name === 'Challenging Behavior'
          )
        )
      ),
    });
  }

  private buildSectionGroup(
    sectionName: string,
    sequenceNumber: number,
    id = 0,
    isSelected = false
  ): FormGroup {
    const group = this.fb.group({
      id,
      sectionName,
      sequenceNumber,
      isSelected,
      isRequired: false,
      isHardStop: false,
      helpText: ['', [Validators.maxLength(4000)]],
    });

    group
      .get('isSelected')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(checked => this.onSectionToggle(group, Boolean(checked)));

    if (DYNAMIC_GATING_SECTION_NAMES.has(sectionName)) {
      group.get('isRequired')?.disable();
      group.get('isHardStop')?.disable();
    }

    // Challenging Behavior is the mandatory start of the behavior workflow —
    // it can never be turned off (see FIXED_CHALLENGING_BEHAVIOR_QUESTIONS /
    // bip-template/CLAUDE.md), so its on/off switch stays permanently on and
    // disabled rather than being locked/unlocked conditionally.
    if (sectionName === 'Challenging Behavior') {
      group.patchValue({ isSelected: true });
      group.get('isSelected')?.disable();
    }

    return group;
  }

  private beginLoading(): void {
    this.pendingRequests++;
    this.loadingSubject.next(true);
  }

  private endLoading(): void {
    this.pendingRequests = Math.max(0, this.pendingRequests - 1);
    if (this.pendingRequests === 0) {
      this.loadingSubject.next(false);
    }
  }

  private handleApiError(error: ErrorResponse): void {
    const errorMessages = error.errors?.map(
      err => err.description || err.detailedMessage
    ) || [error.message || 'An error occurred'];
    this.commonService.notify('error', errorMessages);
  }

  getChallengingBehaviorLookup(onComplete?: () => void): void {
    this.beginLoading();
    this.commonService
      .getDataByType('ChallengingBehaviorOptions', '')
      .pipe(
        takeUntil(this.destroy$),
        // Run onComplete here rather than in `next` so the rest of the load
        // sequence (loadTemplateById / getQuestionnaireItems / getCustomQuestions)
        // still fires even if this lookup errors — otherwise the drawer gets
        // stuck with nothing else ever loading. Call it before endLoading()
        // so a chained beginLoading() overlaps the still-true loading flag
        // instead of briefly flipping it false and re-enabling Save.
        finalize(() => {
          onComplete?.();
          this.endLoading();
        })
      )
      .subscribe({
        next: response => {
          if (response.result) {
            this.originalBehaviorLookup = response.result;
            if (!this.isEditMode && !this.isViewMode) {
              this.challengingBehaviorQuestions =
                this.sortChallengingBehaviorQuestions(
                  response.result.map(item =>
                    this.buildQuestionItem(
                      'behavior',
                      item.id,
                      item.value,
                      true
                    )
                  )
                );
              this.recomputeSectionGating('Challenging Behavior');
            }
          }
          this.cdr.markForCheck();
        },
        error: error => this.handleApiError(error),
      });
  }

  getCustomQuestions(): void {
    if (!this.orgId) {
      return;
    }

    this.beginLoading();
    this.customQuestionService
      .getQuestionsByOrg(this.orgId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.endLoading())
      )
      .subscribe({
        next: response => {
          if (response.result) {
            const questions = Array.isArray(response.result)
              ? response.result
              : [];
            this.mergeCustomQuestions(questions);
          }
          this.cdr.markForCheck();
        },
        error: error => this.handleApiError(error),
      });
  }

  getQuestionnaireItems(): void {
    if (!this.orgId) {
      return;
    }

    this.beginLoading();
    this.manageSettingsService
      .getAllPlans(this.orgId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.endLoading())
      )
      .subscribe({
        next: response => {
          const plans = Array.isArray(response.result)
            ? response.result
            : response.result?.items || [];
          this.mergeQuestionnaireItems(plans);
          this.cdr.markForCheck();
        },
        error: (error: ErrorResponse) => this.handleApiError(error),
      });
  }

  private mergeQuestionnaireItems(plans: PlanPayload[]): void {
    const existingBySourceId = new Map(
      this.questionnaireItems.map(item => [item.sourceId, item])
    );

    const items = plans.map(plan => {
      const sourceId = plan.id || 0;
      const existing = existingBySourceId.get(sourceId);
      const gating = this.originalQuestionnaireGating.get(plan.name);

      return (
        existing ??
        this.buildQuestionItem(
          'plan',
          sourceId,
          plan.name,
          this.originalQuestionnaireSelections.get(plan.name) ?? false,
          gating?.isRequired ?? false,
          gating?.isHardStop ?? false,
          gating?.helpText ?? '',
          plan.hasNoActiveQuestions ?? false
        )
      );
    });

    this.questionnaireItems = this.sortQuestionnaireItemsBySavedOrder(items);
    this.recomputeSectionGating('Questionnaires');
  }

  // A saved template's Questionnaires order (drag-reordered previously) only
  // lives in each dynamic section's sequenceNumber from the get-by-id response
  // (see applyTemplateSections) — getAllPlans() returns the org's plan list in
  // its own order, which has no relation to that saved order, so without
  // re-applying it here every edit reopens with plans back in the org's
  // default order, making a prior reorder look like it was never saved.
  // New/unseen plans (no saved sequence) sort to the end.
  private sortQuestionnaireItemsBySavedOrder(
    items: TemplateQuestionItem[]
  ): TemplateQuestionItem[] {
    if (!this.originalQuestionnaireSequence.size) {
      return items;
    }

    return [...items].sort((a, b) => {
      const seqA =
        this.originalQuestionnaireSequence.get(a.label) ??
        Number.MAX_SAFE_INTEGER;
      const seqB =
        this.originalQuestionnaireSequence.get(b.label) ??
        Number.MAX_SAFE_INTEGER;
      return seqA - seqB;
    });
  }

  private mergeCustomQuestions(questions: QuestionItem[]): void {
    // A behaviorQuestionaries entry with questionId !== 0 is a custom question
    // already sitting inside Challenging Behavior — the API only sends its
    // questionId there, not its text, so backfill the real label now that the
    // org's custom questions have loaded (see applyBehaviorQuestionnaires).
    const questionsById = new Map(questions.map(q => [q.id, q]));
    for (const item of this.challengingBehaviorQuestions) {
      if (item.source === 'custom') {
        const match = questionsById.get(item.sourceId);
        if (match?.customQuestion) {
          item.label = match.customQuestion;
        }
      }
    }

    const alreadyPlacedIds = new Set(
      this.challengingBehaviorQuestions
        .filter(item => item.source === 'custom')
        .map(item => item.sourceId)
    );

    const selectedIds = new Set(
      this.isEditMode || this.isViewMode || this.isCopyMode
        ? this.originalTemplateQuestionIds
        : questions.map(q => q.id)
    );

    this.customQuestions = questions
      .filter(q => q.customQuestion?.trim() && !alreadyPlacedIds.has(q.id))
      .map(q => {
        const templateSettings = this.originalTemplateQuestionSettings.get(
          q.id
        );
        return {
          sequenceNumber:
            templateSettings?.sequenceNumber ?? q.sequenceNumber ?? 0,
          item: this.buildQuestionItem(
            'custom',
            q.id,
            q.customQuestion,
            selectedIds.has(q.id),
            templateSettings?.isRequired ?? false,
            templateSettings?.isHardStop ?? false,
            templateSettings?.helpText ?? ''
          ),
        };
      })
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      .map(entry => entry.item);

    this.recomputeSectionGating('Custom Questions');
  }

  private fixedBehaviorQuestionRank(item: TemplateQuestionItem): number {
    if (item.source !== 'behavior') {
      return -1;
    }

    return FIXED_CHALLENGING_BEHAVIOR_QUESTIONS.findIndex(
      fixed => fixed.sourceId === item.sourceId || fixed.label === item.label
    );
  }

  isFixedBehaviorQuestion(item: TemplateQuestionItem): boolean {
    return this.fixedBehaviorQuestionRank(item) !== -1;
  }

  // Flags the native "Environments" behavior question so the builder can
  // surface a hint about its role in the Strategies "Guide Me" matching.
  isEnvironmentQuestion(item: TemplateQuestionItem): boolean {
    return (
      item.source === 'behavior' &&
      item.sourceId === APP_LOOKUP_IDS.Environmnets
    );
  }

  private sortChallengingBehaviorQuestions(
    items: TemplateQuestionItem[]
  ): TemplateQuestionItem[] {
    const fixed: TemplateQuestionItem[] = [];
    const rest: TemplateQuestionItem[] = [];

    for (const item of items) {
      if (this.isFixedBehaviorQuestion(item)) {
        // Behavior Types is always Required + Hard Stop and can never be
        // excluded — see bip-template/CLAUDE.md.
        item.isIncluded = true;
        item.isRequired = true;
        item.isHardStop = true;
        fixed.push(item);
      } else {
        rest.push(item);
      }
    }

    fixed.sort(
      (a, b) =>
        this.fixedBehaviorQuestionRank(a) - this.fixedBehaviorQuestionRank(b)
    );

    return [...fixed, ...rest];
  }

  private buildQuestionItem(
    source: TemplateQuestionItem['source'],
    sourceId: number,
    label: string,
    isIncluded: boolean,
    isRequired = false,
    isHardStop = false,
    helpText = '',
    hasNoActiveQuestions = false
  ): TemplateQuestionItem {
    return {
      source,
      sourceId,
      label,
      isIncluded,
      isRequired,
      isHardStop,
      helpText,
      hasNoActiveQuestions,
    };
  }

  loadTemplateById(templateId: number): void {
    this.beginLoading();
    this.bipTemplateService
      .getTemplateById(templateId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.endLoading())
      )
      .subscribe({
        next: response => {
          if (response.result) {
            this.applyTemplateData(response.result);
          }
        },
        error: (error: ErrorResponse) => this.handleApiError(error),
      });
  }

  private applyTemplateData(templateData: TemplateModel): void {
    this.templateData = templateData;
    this.templateForm.patchValue({
      // Copy mode reuses every other field from the source template as-is,
      // but the name must stay unique per template — leave it blank instead
      // of pre-filling it with the source name.
      templateName: this.isCopyMode ? '' : templateData.templateName,
      isDefault: templateData.isDefault,
      isIncludeStrategyGuidelines: templateData.isIncludeStrategyGuidelines,
      organizationId: this.orgId,
    });

    const templateQuestions = templateData.templateQuestions || [];
    const hasTemplateQuestionSettings = templateQuestions.length > 0;

    this.originalTemplateQuestionSettings = new Map(
      hasTemplateQuestionSettings
        ? templateQuestions.map(q => [q.questionId, q])
        : []
    );
    this.originalTemplateQuestionIds = hasTemplateQuestionSettings
      ? Array.from(this.originalTemplateQuestionSettings.keys())
      : templateData.templateQuestionIds || [];
    this.applyTemplateFields(templateData.templateFields);
    this.applyTemplateSections(templateData.templateSections);
    this.applyBehaviorQuestionnaires(templateData.behaviorQuestionaries);

    if (this.orgId) {
      this.getCustomQuestions();
      this.getQuestionnaireItems();
    }

    this.cdr.markForCheck();
  }

  private applyTemplateFields(fields: TemplateField[]): void {
    if (!fields?.length) {
      return;
    }

    const fieldsArray = this.templateFields;
    for (const control of fieldsArray.controls) {
      const match = fields.find(f => f.fieldName === control.value.fieldName);
      if (match) {
        control.patchValue({ id: match.id, isSelected: match.isSelected });
      }
    }
  }

  private applyTemplateSections(templateSections: TemplateSection[]): void {
    if (!templateSections?.length) {
      return;
    }

    // Section order is fixed by STATIC_SECTION_NAMES and never reordered —
    // only match on sectionName and re-derive sequenceNumber from that fixed order.
    const staticNames = new Set<string>(STATIC_SECTION_NAMES);
    const dynamicSections = templateSections.filter(
      section => !staticNames.has(section.sectionName)
    );

    const sectionsArray = this.sections;
    for (const control of sectionsArray.controls) {
      const match = templateSections.find(
        section => section.sectionName === control.value.sectionName
      );
      if (match) {
        control.patchValue({
          id: match.id,
          // Challenging Behavior can never be turned off, even for a legacy
          // saved template that predates this rule — see buildSectionGroup.
          isSelected:
            control.value.sectionName === 'Challenging Behavior'
              ? true
              : match.isSelected,
          isRequired: match.isRequired ?? false,
          isHardStop: match.isHardStop ?? false,
          helpText: match.helpText ?? '',
        });
      }
    }

    for (const [index, ctrl] of sectionsArray.controls.entries()) {
      ctrl.patchValue({ sequenceNumber: index + 1 });
    }

    const customQuestionsSection = this.getSectionControl('Custom Questions');
    if (customQuestionsSection?.value.isHardStop) {
      this.syncChallengingBehaviorHardStopLock(true, false);
    }

    this.originalQuestionnaireSelections = new Map(
      dynamicSections.map(section => [section.sectionName, section.isSelected])
    );
    this.originalQuestionnaireSequence = new Map(
      dynamicSections.map(section => [
        section.sectionName,
        section.sequenceNumber,
      ])
    );
    this.originalQuestionnaireGating = new Map(
      dynamicSections.map(section => [
        section.sectionName,
        {
          isRequired: section.isRequired ?? false,
          isHardStop: section.isHardStop ?? false,
          helpText: section.helpText ?? '',
        },
      ])
    );
  }

  private applyBehaviorQuestionnaires(
    behaviorQuestionaries: BehaviorQuestionnaires[]
  ): void {
    const sorted = [...(behaviorQuestionaries || [])].sort((a, b) => {
      if (a.behaviorTypeSequenceNumber !== b.behaviorTypeSequenceNumber) {
        return a.behaviorTypeSequenceNumber - b.behaviorTypeSequenceNumber;
      }
      return (a.questionSequenceNumber || 0) - (b.questionSequenceNumber || 0);
    });

    const mapped = this.withMissingSectionHeaders(sorted).map(entry => {
      if (!entry.questionId) {
        const lookupLabel = this.originalBehaviorLookup.find(
          lookup => lookup.id === entry.applicationObjectId
        )?.value;

        return this.buildQuestionItem(
          'behavior',
          entry.applicationObjectId || entry.sectionId,
          lookupLabel || entry.value || '',
          entry.isSelected,
          entry.isRequired ?? false,
          entry.isHardStop ?? false,
          entry.helpText ?? ''
        );
      }

      // A custom question already placed in Challenging Behavior can never be
      // deselected — see customQuestionsEnterPredicate / dropQuestion.
      return this.buildQuestionItem(
        'custom',
        entry.questionId,
        entry.value || '',
        true,
        entry.isRequired ?? false,
        entry.isHardStop ?? false,
        entry.helpText ?? ''
      );
    });

    this.challengingBehaviorQuestions = this.sortChallengingBehaviorQuestions(
      this.withFixedBehaviorQuestions(mapped)
    );
    this.recomputeSectionGating('Challenging Behavior');
  }

  // A custom question's raw entry carries its parent section only via
  // applicationObjectId — it has no header (questionId 0) row of its own. If
  // that header is missing from the saved data (seen in practice for section
  // ids beyond just the two fixed ones), the section disappears entirely from
  // the builder while its question still renders, and the grouping is lost on
  // the next save (mapBehaviorQuestionnairesForPayload infers a custom
  // question's section from the nearest preceding 'behavior' item, so an
  // orphaned question would get silently reattached to the wrong section).
  // Synthesize the missing header immediately before the first question that
  // references it.
  private withMissingSectionHeaders(
    entries: BehaviorQuestionnaires[]
  ): BehaviorQuestionnaires[] {
    const existingSections = new Set(
      entries
        .filter(entry => !entry.questionId)
        .map(entry => entry.applicationObjectId || entry.sectionId)
    );

    const result: BehaviorQuestionnaires[] = [];
    for (const entry of entries) {
      const sectionId = entry.applicationObjectId || entry.sectionId;

      if (entry.questionId && !existingSections.has(sectionId)) {
        const lookupLabel = this.originalBehaviorLookup.find(
          lookup => lookup.id === sectionId
        )?.value;

        result.push({
          sectionId,
          questionId: 0,
          isSelected: entry.isSelected,
          behaviorTypeSequenceNumber: entry.behaviorTypeSequenceNumber,
          questionSequenceNumber: 0,
          applicationObjectId: sectionId,
          value: lookupLabel || '',
        });
        existingSections.add(sectionId);
      }

      result.push(entry);
    }

    return result;
  }

  // Behavior Types is always Required + Hard Stop and can never be removed
  // from a template (see bip-template/CLAUDE.md). A saved template's
  // behaviorQuestionaries can come back without its header row — from a
  // legacy template, or a partial save. Without this, the fixed question
  // silently disappears from the builder and would be dropped from the
  // template on the next save.
  private withFixedBehaviorQuestions(
    items: TemplateQuestionItem[]
  ): TemplateQuestionItem[] {
    const missing = FIXED_CHALLENGING_BEHAVIOR_QUESTIONS.filter(
      fixed =>
        !items.some(
          item => item.source === 'behavior' && item.sourceId === fixed.sourceId
        )
    ).map(fixed =>
      this.buildQuestionItem(
        'behavior',
        fixed.sourceId,
        this.originalBehaviorLookup.find(lookup => lookup.id === fixed.sourceId)
          ?.value || fixed.label,
        true,
        true,
        true
      )
    );

    return missing.length ? [...items, ...missing] : items;
  }

  toggleTemplateField(index: number, checked: boolean): void {
    if (this.isViewMode) {
      return;
    }
    this.templateFields.at(index).patchValue({ isSelected: checked });
  }

  onSectionToggle(group: AbstractControl, checked: boolean): void {
    const sectionName = group.value.sectionName;

    if (!checked) {
      group.patchValue({ isRequired: false, isHardStop: false });

      // Turning the whole section off must reset every sub-item's Required/
      // Hard Stop too — otherwise recomputeSectionGating brings the section's
      // own flags straight back the moment it's turned back on, since it
      // derives them from these same (still-checked) sub-items.
      if (DYNAMIC_GATING_SECTION_NAMES.has(sectionName)) {
        this.resetSubItemGating(sectionName);
      }

      // Clearing isHardStop here goes through patchValue, not the
      // (nzCheckedChange) handler, so it won't call onSectionHardStopChange —
      // release the Challenging Behavior lock explicitly or it stays
      // selected/required/hard-stop and disabled with nothing left requiring it.
      if (sectionName === 'Custom Questions') {
        this.syncChallengingBehaviorHardStopLock(false);
      }
    } else if (DYNAMIC_GATING_SECTION_NAMES.has(sectionName)) {
      // Re-selecting a section with a sub-item list restores its computed
      // Required/Hard Stop from the sub-items instead of leaving the
      // false values set above when it was last turned off.
      this.recomputeSectionGating(sectionName);
    }
  }

  // Behavior Types is exempt — it is always Required + Hard Stop and can
  // never be excluded (see FIXED_CHALLENGING_BEHAVIOR_QUESTIONS).
  private resetSubItemGating(sectionName: string): void {
    const items = this.getDynamicSectionItems(sectionName);
    if (!items) {
      return;
    }

    for (const item of items) {
      if (this.isFixedBehaviorQuestion(item)) {
        continue;
      }
      item.isRequired = false;
      item.isHardStop = false;
    }
  }

  // Sections with a sub-item list never let the user set Required/Hard Stop
  // directly (see DYNAMIC_GATING_SECTION_NAMES / buildSectionGroup) — the
  // section is Required/Hard Stop whenever any included sub-item is. Only
  // this path (user editing sub-items) can surface the Challenging Behavior
  // notice below — the direct recomputeSectionGating() calls elsewhere run
  // during data load and stay silent.
  private recomputeAllSectionGating(): void {
    DYNAMIC_GATING_SECTION_NAMES.forEach(name =>
      this.recomputeSectionGating(name, true)
    );
  }

  private recomputeSectionGating(sectionName: string, notify = false): void {
    const items = this.getDynamicSectionItems(sectionName);
    if (!items) {
      return;
    }

    const section = this.getSectionControl(sectionName);
    if (!section) {
      return;
    }

    const wasHardStop = Boolean(section.value.isHardStop);
    const isRequired = items.some(item => item.isIncluded && item.isRequired);
    const isHardStop = items.some(item => item.isIncluded && item.isHardStop);
    section.patchValue({ isRequired, isHardStop });

    if (sectionName === 'Custom Questions') {
      // Notify only on the rising edge (Custom Questions just became Hard
      // Stop) so the warning fires once per cause instead of on every
      // unrelated sub-item toggle that re-runs this recompute.
      this.syncChallengingBehaviorHardStopLock(
        isHardStop,
        notify && isHardStop && !wasHardStop
      );
    }
  }

  private getDynamicSectionItems(
    sectionName: string
  ): TemplateQuestionItem[] | null {
    switch (sectionName) {
      case 'Challenging Behavior':
        return this.challengingBehaviorQuestions;
      case 'Custom Questions':
        return this.customQuestions;
      case 'Questionnaires':
        return this.questionnaireItems;
      default:
        return null;
    }
  }

  // Only reachable for sections without a sub-item list (Behavior Screener,
  // Strategy Details, Reports) — Challenging Behavior/Custom Questions/
  // Questionnaires keep these checkboxes permanently disabled and computed
  // via recomputeSectionGating instead (see DYNAMIC_GATING_SECTION_NAMES).
  onSectionHardStopChange(group: AbstractControl, checked: boolean): void {
    if (checked) {
      group.patchValue({ isRequired: true });
    }
  }

  private getSectionControl(sectionName: string): FormGroup | undefined {
    return this.sections.controls.find(
      ctrl => ctrl.value.sectionName === sectionName
    ) as FormGroup | undefined;
  }

  // Custom Questions can be interspersed anywhere in the behavior workflow, so
  // a Custom Questions Hard Stop can only be honored if Challenging Behavior —
  // which always precedes it — is also a Hard Stop. Lock it so it can't be
  // relaxed out from under that guarantee.
  private syncChallengingBehaviorHardStopLock(
    locked: boolean,
    notify = true
  ): void {
    const challengingBehavior = this.getSectionControl('Challenging Behavior');
    if (!challengingBehavior) {
      return;
    }

    // isSelected/isRequired/isHardStop are already permanently disabled and
    // forced on (see buildSectionGroup) — Challenging Behavior can never be
    // turned off at all now, so there is nothing left to lock/unlock here for
    // Custom Questions' Hard Stop precondition beyond this notice.
    if (locked && notify) {
      this.commonService.notify(
        'warning',
        'Challenging Behavior must stay Hard Stop while Custom Questions is Hard Stop.'
      );
    }
  }

  // Only reachable for sections without a sub-item list — see
  // onSectionHardStopChange.
  onSectionRequiredChange(group: AbstractControl, checked: boolean): void {
    if (!checked) {
      group.patchValue({ isHardStop: false });
    }
  }

  // Unchecking a question resets its own Required/Hard Stop back to
  // unchecked instead of leaving them set in the background — the section's
  // computed Required/Hard Stop (recomputeAllSectionGating) must also drop
  // once nothing included underneath it still needs them.
  toggleQuestionIncluded(item: TemplateQuestionItem, checked: boolean): void {
    if (this.isViewMode || this.isFixedBehaviorQuestion(item)) {
      return;
    }

    item.isIncluded = checked;
    if (!checked) {
      item.isRequired = false;
      item.isHardStop = false;
    }
    this.recomputeAllSectionGating();
  }

  onQuestionHardStopChange(item: TemplateQuestionItem, checked: boolean): void {
    if (this.isViewMode || this.isFixedBehaviorQuestion(item)) {
      return;
    }

    item.isHardStop = checked;
    if (checked) {
      item.isRequired = true;
    }
    this.recomputeAllSectionGating();
  }

  onQuestionRequiredChange(item: TemplateQuestionItem, checked: boolean): void {
    if (this.isViewMode || this.isFixedBehaviorQuestion(item)) {
      return;
    }

    item.isRequired = checked;
    if (!checked) {
      item.isHardStop = false;
    }
    this.recomputeAllSectionGating();
  }

  updateQuestionHelpText(item: TemplateQuestionItem, value: string): void {
    if (this.isViewMode) {
      return;
    }
    item.helpText = value;
  }

  toggleHelp(key: string): void {
    if (this.openHelpKeys.has(key)) {
      this.openHelpKeys.delete(key);
    } else {
      this.openHelpKeys.add(key);
    }
  }

  isHelpOpen(key: string): boolean {
    return this.openHelpKeys.has(key);
  }

  customQuestionsEnterPredicate = (
    drag: CdkDrag<TemplateQuestionItem>
  ): boolean => {
    return drag.data?.source !== 'behavior';
  };

  challengingBehaviorSortPredicate = (
    index: number,
    drag: CdkDrag<TemplateQuestionItem>
  ): boolean => {
    if (drag.data && this.isFixedBehaviorQuestion(drag.data)) {
      return false;
    }

    const fixedCount = this.challengingBehaviorQuestions.filter(item =>
      this.isFixedBehaviorQuestion(item)
    ).length;

    return index >= fixedCount;
  };

  dropQuestion(event: CdkDragDrop<TemplateQuestionItem[]>): void {
    if (this.isViewMode) {
      return;
    }

    const draggedItem = event.item.data as TemplateQuestionItem;
    if (this.isFixedBehaviorQuestion(draggedItem)) {
      return;
    }

    if (event.previousContainer === event.container) {
      if (event.previousIndex === event.currentIndex) {
        return;
      }
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      const movedItem = event.previousContainer.data[event.previousIndex];
      if (
        event.container.id === 'customQuestionsList' &&
        movedItem.source === 'behavior'
      ) {
        this.commonService.notify(
          'error',
          'Challenging Behavior questions cannot be moved to Custom Questions.'
        );
        return;
      }

      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );

      if (event.container.id === 'challengingBehaviorList') {
        movedItem.isIncluded = true;
      }
    }

    this.recomputeAllSectionGating();
    this.cdr.markForCheck();
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByQuestion(index: number, item: TemplateQuestionItem): string {
    return `${item.source}-${item.sourceId}`;
  }

  trackBySectionName(index: number, control: AbstractControl): string {
    return control.value.sectionName;
  }

  onCancel(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.ADD_TEMPLATE_V2);
  }

  submit(): void {
    if (this.isViewMode) {
      return;
    }

    if (!this.orgId || this.orgId <= 0) {
      this.commonService.notify(
        'error',
        'Please select a valid school before saving.'
      );
      return;
    }

    if (!this.isFormValid) {
      return;
    }

    this.lastSubmittedPayload = this.buildPayload();

    const isEdit = this.isEditMode && !!this.templateId;
    const isCreate = !isEdit;
    const oldAuditValue: Record<string, unknown> | null = isCreate
      ? null
      : {
          templateName: this.templateData?.templateName,
          isDefault: this.templateData?.isDefault ?? false,
          isIncludeStrategyGuidelines:
            this.templateData?.isIncludeStrategyGuidelines ?? false,
          templateFields: this.templateData?.templateFields || [],
          templateSections: this.templateData?.templateSections || [],
          templateQuestions: this.templateData?.templateQuestions || [],
          behaviorQuestionaries: this.templateData?.behaviorQuestionaries || [],
        };

    const newAuditValue: Record<string, unknown> = {
      templateName: this.lastSubmittedPayload.templateName,
      isDefault: this.lastSubmittedPayload.isDefault,
      isIncludeStrategyGuidelines:
        this.lastSubmittedPayload.isIncludeStrategyGuidelines,
      templateFields: this.lastSubmittedPayload.templateFields,
      templateSections: this.lastSubmittedPayload.templateSections,
      templateQuestions: this.lastSubmittedPayload.templateQuestions,
      behaviorQuestionaries: this.lastSubmittedPayload.behaviorQuestionaries,
    };

    const _modifiedFields = this.buildModifiedFields(
      oldAuditValue,
      newAuditValue,
      isCreate
    );

    this.lastSubmittedPayload = {
      ...this.lastSubmittedPayload,
      _modifiedFields,
    };

    this.beginLoading();
    const operation$ = isEdit
      ? this.bipTemplateService.editTemplate(
          this.lastSubmittedPayload,
          this.templateId as number
        )
      : this.bipTemplateService.createTemplate(this.lastSubmittedPayload);

    operation$
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.endLoading())
      )
      .subscribe({
        next: () => {
          if (isEdit && this.templateId) {
            this.bipTemplateGatingService.invalidate(this.templateId);
          }
          this.commonService.notify(
            'success',
            isEdit
              ? 'Behavior Template updated successfully.'
              : 'Behavior Template created successfully.'
          );
          this.drawerService.closeDrawer(DRAWER_IDS.ADD_TEMPLATE_V2);
        },
        error: (error: ErrorResponse) => this.handleApiError(error),
      });
  }

  private buildPayload(): BipTemplateSavePayload {
    const formValue = this.templateForm.getRawValue();

    // Static sections (Challenging Behavior, Custom Questions, etc.) are sent
    // as-is; each questionnaire item becomes its own sub-questionnaire section,
    // continuing the sequence after the static ones (matches the BipTemplate API).
    const staticSections: TemplateSectionPayload[] = formValue.sections.map(
      (section: TemplateSectionPayload) => ({
        sectionName: section.sectionName,
        sequenceNumber: section.sequenceNumber,
        isSelected: section.isSelected,
        isRequired: section.isRequired,
        isHardStop: section.isHardStop,
        isSubQuestionnaireSection: false,
        helpText: section.helpText,
      })
    );

    const questionnaireSections: TemplateSectionPayload[] =
      this.questionnaireItems.map((item, index) => ({
        sectionName: item.label,
        sequenceNumber: staticSections.length + index + 1,
        isSelected: item.isIncluded,
        isRequired: item.isRequired,
        isHardStop: item.isHardStop,
        isSubQuestionnaireSection: true,
        helpText: item.helpText,
        // item.sourceId is the plan's `id` from getAllPlans (see mergeQuestionnaireItems).
        planTypeId: item.sourceId,
      }));

    const templateFields: TemplateFieldPayload[] = formValue.templateFields.map(
      (field: TemplateField) => ({
        fieldName: field.fieldName,
        isSelected: field.isSelected,
      })
    );

    return {
      templateName: formValue.templateName,
      organizationId: this.orgId || 0,
      isDefault: formValue.isDefault,
      isIncludeStrategyGuidelines: formValue.isIncludeStrategyGuidelines,
      templateSections: [...staticSections, ...questionnaireSections],
      templateFields,
      templateQuestions: this.mapCustomQuestionsForPayload(
        this.templateQuestionCandidates
      ),
      behaviorQuestionaries: this.mapBehaviorQuestionnairesForPayload(
        this.challengingBehaviorQuestions
      ),
    };
  }

  // Only included custom questions are saved as template questions. Sequence
  // must be sent explicitly — the backend does not infer it from array order
  // and drops it on template update otherwise (see bip-template/CLAUDE.md).
  private mapCustomQuestionsForPayload(
    items: TemplateQuestionItem[]
  ): TemplateQuestionPayload[] {
    return items
      .filter(item => item.isIncluded)
      .map((item, index) => ({
        questionId: item.sourceId,
        sequenceNumber: index + 1,
        isRequired: item.isRequired,
        isHardStop: item.isHardStop,
        helpText: item.helpText,
      }));
  }

  // Native behavior lookups use applicationObjectId; custom questions
  // dragged into Challenging Behavior use questionId instead (see CLAUDE.md).
  // A custom question is answered per-behavior, so it inherits the
  // applicationObjectId of the nearest preceding native behavior question in
  // the list — consecutive custom questions under the same behavior item all
  // share that same value.
  private mapBehaviorQuestionnairesForPayload(
    items: TemplateQuestionItem[]
  ): BehaviorQuestionnairePayload[] {
    let currentSectionId = 0;
    let behaviorTypeSequenceNumber = 0;
    let questionSequenceNumber = 0;

    return items.map(item => {
      const isCustomQuestion = item.source === 'custom';

      if (!isCustomQuestion) {
        currentSectionId = item.sourceId;
        behaviorTypeSequenceNumber += 1;
        questionSequenceNumber = 1;
      } else {
        questionSequenceNumber += 1;
      }

      return {
        questionId: isCustomQuestion ? item.sourceId : 0,
        applicationObjectId: isCustomQuestion
          ? currentSectionId
          : item.sourceId,
        behaviorTypeSequenceNumber,
        questionSequenceNumber,
        isSelected: item.isIncluded,
        isRequired: item.isRequired,
        isHardStop: item.isHardStop,
        helpText: item.helpText,
      };
    });
  }

  private buildModifiedFields(
    oldValue: Record<string, unknown> | null,
    newValue: Record<string, unknown>,
    isCreate: boolean
  ): AuditFieldChange[] {
    const yn = (val: boolean) => (val ? 'Yes' : 'No');

    const questionLabelMap = new Map<number, string>();
    for (const item of [
      ...this.customQuestions,
      ...this.challengingBehaviorQuestions,
    ]) {
      questionLabelMap.set(item.sourceId, item.label);
    }

    const asRecords = (arr: unknown): Record<string, unknown>[] =>
      Array.isArray(arr) ? (arr as Record<string, unknown>[]) : [];

    const toDisplayText = (value: unknown): string => {
      if (value === null || value === undefined) {
        return '';
      }

      if (typeof value === 'string') {
        return value;
      }

      if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
      }

      return JSON.stringify(value);
    };

    const formatFields = (arr: unknown): string =>
      asRecords(arr)
        .map(
          field =>
            `${toDisplayText(field['fieldName'])}: ${yn(Boolean(field['isSelected']))}`
        )
        .join(', ');

    // Order-aware — reflects the current section sequence.
    const formatSections = (arr: unknown): string =>
      asRecords(arr)
        .map(
          (section, index) =>
            `${index + 1}. ${toDisplayText(section['sectionName'])}: Selected=${yn(Boolean(section['isSelected']))}, Required=${yn(Boolean(section['isRequired']))}, Hard Stop=${yn(Boolean(section['isHardStop']))}`
        )
        .join(', ');

    const resolveQuestionLabel = (questionId: number): string =>
      questionLabelMap.get(questionId) || `Unknown (${questionId})`;

    // Order-aware — reflects the current custom-question sequence.
    const formatQuestions = (arr: unknown): string =>
      asRecords(arr)
        .map(
          (question, index) =>
            `${index + 1}. ${resolveQuestionLabel(Number(question['questionId']))}: Required=${yn(Boolean(question['isRequired']))}, Hard Stop=${yn(Boolean(question['isHardStop']))}`
        )
        .join(', ');

    // The old (API) shape carries its own label in `value` — prefer that over
    // the current in-memory question map so a renamed/removed question still
    // shows the name it had at the time, not today's name or a raw id. The new
    // payload shape (BehaviorQuestionnairePayload) has no `value`, so it always
    // falls through to the live lookup.
    const resolveBehaviorLabel = (item: Record<string, unknown>): string => {
      const rawValue = item['value'];
      if (typeof rawValue === 'string' && rawValue.trim().length > 0) {
        return rawValue;
      }

      const questionId = Number(item['questionId'] || 0);
      if (questionId > 0) {
        return resolveQuestionLabel(questionId);
      }

      const applicationObjectId = Number(item['applicationObjectId'] || 0);
      return (
        this.originalBehaviorLookup.find(
          lookup => lookup.id === applicationObjectId
        )?.value || `Unknown (${applicationObjectId})`
      );
    };

    // Order-aware — reflects the current Challenging Behavior question sequence.
    const formatBehaviorQuestionnaires = (arr: unknown): string =>
      asRecords(arr)
        .map(
          (item, index) =>
            `${index + 1}. ${resolveBehaviorLabel(item)}: Selected=${yn(Boolean(item['isSelected']))}, Required=${yn(Boolean(item['isRequired']))}, Hard Stop=${yn(Boolean(item['isHardStop']))}`
        )
        .join(', ');

    const normalizeAuditValue = (
      source: Record<string, unknown>
    ): Record<string, unknown> => ({
      templateName: source['templateName'] ?? '',
      isDefault: yn(Boolean(source['isDefault'])),
      isIncludeStrategyGuidelines: yn(
        Boolean(source['isIncludeStrategyGuidelines'])
      ),
      templateFields: formatFields(source['templateFields']),
      templateSections: formatSections(source['templateSections']),
      templateQuestions: formatQuestions(source['templateQuestions']),
      behaviorQuestionaries: formatBehaviorQuestionnaires(
        source['behaviorQuestionaries']
      ),
    });

    const original = oldValue ? normalizeAuditValue(oldValue) : {};
    const updated = normalizeAuditValue(newValue);

    const changes = this.auditContextService.buildFieldChanges(
      original,
      updated,
      {
        sensitiveFields: [],
        labelMap: {
          templateName: 'Template Name',
          isDefault: 'Default Template',
          isIncludeStrategyGuidelines: 'Include Strategy Guidelines',
          templateFields: 'Template Fields',
          templateSections: 'Template Sections',
          templateQuestions: 'Custom Questions',
          behaviorQuestionaries: 'Behavior Questionnaires',
        },
      }
    );

    if (isCreate) {
      return changes;
    }

    return changes.filter(change => change.oldValue !== change.newValue);
  }
}
